import InputFloatingLabel from 'components/InputFloatingLabel';
import React, { ChangeEvent, FormEvent, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { renderToast } from 'utils/basic/renderToast';
import {
  BorderTable,
  Container,
  ContainerButton,
  ContainerInputs,
  DeleteButton,
  DragDropButton,
  DragDropContainer,
  DragDropText,
  ErrorMessage,
  FileInput,
  Form,
  FormGroup,
  FormJustification,
  FullScreenLoader,
  Label,
  LoadingSpinner,
  PreviewContainer,
  PreviewImage,
  SubmitButton,
  TextArea,
  Title
} from './styles';
import { FormData, FormErrors } from './types';
import { sendInvoiceInContingence } from 'Functions/invoiceContingenceService';

export const UploadImageContigence: React.FC = () => {
  const token = localStorage.getItem('token');
  const tokenParse = token ? JSON.parse(token) : null;
  const UserLogged = tokenParse && tokenParse.idTokenClaims && tokenParse.idTokenClaims.name;

  const [image, setImage] = useState<File | null>(null);
  const [imageBase64, setImageBase64] = useState<string>('');
  const [formData, setFormData] = useState<FormData>({
    invoiceNumber: '',
    serialNumber: '',
    user: UserLogged || '',
    justification: '',
    responsible: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const tries = useRef<number>(0);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const navigate = useNavigate();

  const now = new Date();
  const formattedDate = now.toLocaleDateString('pt-BR');
  const formattedTime = now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });



  const handleImageChange = (file: File) => {
    if (file) {
      if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
        setErrors({ ...errors, image: 'Formato de imagem inválido. Use JPG, JPEG ou PNG.' });
        return;
      }

      setImage(file);
      setErrors({ ...errors, image: '' });

      const reader = new FileReader();
      reader.onloadend = () => {
        setImageBase64(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageChange(file);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleImageChange(file);
    }
  }, []);

  const handleChange = (name: string, value: string) => {
    if (name === 'invoiceNumber') {
      if (value && !/^\d*$/.test(value)) return;
      if (value.length > 10) return;
    }

    if (name === 'serialNumber') {
      if (value && !/^\d*$/.test(value)) return;
      if (value.length > 4) return;
    }

    if (name === 'justification') {

      if (value.length > 100) return;
    }

    setFormData({
      ...formData,
      [name]: value
    });

    if (errors[name as keyof FormErrors]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!image) {
      newErrors.image = 'Por favor, selecione uma imagem';
    }

    if (!formData.invoiceNumber.trim()) {
      newErrors.invoiceNumber = 'Número da Nota Fiscal é obrigatório';
    } else if (!/^\d{1,10}$/.test(formData.invoiceNumber)) {
      newErrors.invoiceNumber = 'Deve conter até 10 dígitos numéricos';
    }

    if (!formData.serialNumber.trim()) {
      newErrors.serialNumber = 'Número de Série da Nota é obrigatório';
    } else if (!/^\d{1,4}$/.test(formData.serialNumber)) {
      newErrors.serialNumber = 'Deve conter até 4 dígitos numéricos';
    }

    if (!formData.justification.trim()) {
      newErrors.justification = 'Justificativa é obrigatória';
    } else if (formData.justification.length > 100) {
      newErrors.justification = 'Máximo de 100 caracteres excedido';
    }

    if (!formData.responsible.trim()) {
      newErrors.responsible = 'Responsável pelo Recebimento é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (validateForm()) {
        setIsLoading(true);
        const base64Data = imageBase64.split(',')[1] || '';

        const response = await sendInvoiceInContingence(
          {
            numeroNF: formData.invoiceNumber,
            numeroSerie: formData.serialNumber,
            nmRespReceb: formData.responsible,
            justificativa: formData.justification,
            image: base64Data
          },
          UserLogged
        );

        if (response.statusCode === 200 || response.statusCode === 201) {
          setIsLoading(false);
          renderToast(
            `Canhoto enviado com sucesso! NF ${formData.invoiceNumber} associada às ${formattedDate} ${formattedTime}`,
            {
              type: 'success', duration: 5000,
            }
          );
          setFormData({
            invoiceNumber: '',
            serialNumber: '',
            user: UserLogged || '',
            justification: '',
            responsible: ''
          });
          setImage(null);
          setImageBase64('');
          tries.current = 0;
          return;
        } else {
          setIsLoading(false);
          if (tries.current >= 3) {
            renderToast(
              `Erro ao enviar canhoto. Por favor entregar o documento fisicamente na expedição.`,
              {
                type: 'warning', duration: 5000,

                onClose: () => {
                  navigate('/canhoto-digital');
                }
              }
            );
            setIsLoading(false);
            tries.current = 0;
            return;
          } else {
            tries.current += 1;
            renderToast(
              response.body,
              {
                type: 'warning', duration: 5000,

              }
            );
            setIsLoading(false);
            return;
          }
        }
      }
    } catch (error: any) {
      tries.current += 1;
      setIsLoading(false);
      if (tries.current >= 3) {
        renderToast(
          `Erro ao enviar canhoto. Por favor entregar o documento fisicamente na expedição.`,
          {
            type: 'warning', duration: 5000,

            onClose: () => {
              navigate('/canhoto-digital');
            }
          }
        );
        setIsLoading(false);
        tries.current = 0;
        return;
      }
      renderToast(
        `Não foi possível validar os dados do canhoto. Verifique a NF e o número de série e tente novamente.`,
        {
          type: 'error', duration: 5000
        });
    }
  };

  const handleDeleteImage = () => {
    setImage(null);
    setImageBase64('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      {isLoading && (
        <FullScreenLoader>
          <LoadingSpinner />
        </FullScreenLoader>
      )}
      <Container>
        <BorderTable>
          <Title>Upload de Canhoto Físico</Title>
          <Form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <ContainerInputs>
                <FormGroup>
                  <InputFloatingLabel
                    type="text"
                    id="invoiceNumber"
                    label="Digite o número da NF *"
                    value={formData.invoiceNumber}
                    onChange={(e) => handleChange('invoiceNumber', e.target.value)}
                    error={!!errors.invoiceNumber}
                    helperText={errors.invoiceNumber}
                    inputMode="numeric"
                  />
                </FormGroup>
                <FormGroup>
                  <InputFloatingLabel
                    type="text"
                    id="serialNumber"
                    label="Digite o número de série *"
                    value={formData.serialNumber}
                    onChange={(e) => handleChange('serialNumber', e.target.value)}
                    error={!!errors.serialNumber}
                    helperText={errors.serialNumber}
                    inputMode="numeric"
                  />
                </FormGroup>
                <FormGroup>
                  <InputFloatingLabel
                    type="text"
                    id="responsible"
                    label="Responsável pelo Recebimento *"
                    value={formData.responsible}
                    onChange={(e) => handleChange('responsible', e.target.value)}
                    error={!!errors.responsible}
                    helperText={errors.responsible}
                  />
                </FormGroup>
              </ContainerInputs>
              <FormJustification>
                <TextArea
                  id="justification"
                  name="justification"
                  value={formData.justification}
                  onChange={(e) => handleChange('justification', e.target.value)}
                  placeholder="Descreva o motivo do envio do canhoto físico"
                  maxLength={100}
                />
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  {formData.justification.length}/100 caracteres
                </div>
                {errors.justification && <ErrorMessage>{errors.justification}</ErrorMessage>}
              </FormJustification>
            </div>

            <ContainerInputs>
              <Label>Imagem do Canhoto</Label>
              {!imageBase64 ? (
                <DragDropContainer
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  isDragging={isDragging}
                >
                  <DragDropText>
                    Arraste e solte sua imagem aqui ou
                  </DragDropText>
                  <DragDropButton type="button" onClick={triggerFileInput}>
                    Selecione uma imagem
                  </DragDropButton>
                  <FileInput
                    type="file"
                    accept=".png,.jpg,.jpeg"
                    onChange={handleFileInputChange}
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                  />
                </DragDropContainer>
              ) : (
                <PreviewContainer>
                  <PreviewImage
                    src={imageBase64}
                    alt="Preview"
                    show={!!imageBase64}
                  />
                  <DeleteButton onClick={handleDeleteImage}>X</DeleteButton>
                </PreviewContainer>
              )}
              {errors.image && <ErrorMessage>{errors.image}</ErrorMessage>}

            </ContainerInputs>

            <ContainerButton>
              <SubmitButton
                disabled={isLoading}
                typeStyle="default"
                data-testid="button-new-version"
                type="submit"
              >
                Enviar
              </SubmitButton>
            </ContainerButton>
          </Form>
        </BorderTable>
      </Container>
    </>
  );
};